import * as React from 'react';
import { Loader2, PlusCircle, Trash2 } from 'lucide-react';
import {
  CUSTOM_PARSERS,
  allowedCustomParsers,
  legacyCustomParser,
  type EnterpriseDetail,
  type EnterpriseEnumValue,
  type EnterpriseSettingsUpdateInput,
} from '@proptr/shared';
import { Button } from '../../components/ui/button.js';
import { Card } from '../../components/ui/card.js';
import { Input } from '../../components/ui/input.js';
import { Label } from '../../components/ui/label.js';
import { Select } from '../../components/ui/select.js';
import { Switch } from '../../components/ui/switch.js';
import { client } from '../../main.js';
import { cn } from '../../lib/utils.js';
import { useAgencyContext } from '../AgencyDetailPage.js';

// Parser-type catalog. Mirrors legacy enterprise.js ~lines 3260-3286.
// `legacy === true` enterprises see project variants too.
const PARSER_TYPES_FULL: EnterpriseEnumValue[] = [
  { id: 2, name: 'XML parser – UNITS', value: 'xml' },
  { id: 6, name: 'XML parser – PROJECTS', value: 'xml_project' },
  { id: 1, name: 'CSV parser – Google Sheets UNITS', value: 'csv_unit' },
  { id: 3, name: 'CSV parser – Google Sheets PROJECTS', value: 'csv_project' },
  { id: 5, name: 'Pixxi CRM – UNITS', value: 'pixxi_unit' },
  { id: 4, name: 'Pixxi CRM – PROJECTS', value: 'pixxi_project' },
  { id: 7, name: 'PropSpace CRM – UNITS', value: 'propspace_unit' },
  { id: 8, name: 'Property Finder CRM - UNITS', value: 'propertyfinder_unit' },
  { id: 9, name: 'Kendal CRM - UNITS', value: 'kendal_unit' },
  { id: 10, name: 'Propfusion CRM - UNITS', value: 'propfusion_unit' },
  { id: 11, name: 'GoYzer CRM - UNITS', value: 'goyzer_unit' },
];
const PARSER_TYPES_NON_LEGACY: EnterpriseEnumValue[] =
  PARSER_TYPES_FULL.filter(
    (o) =>
      o.value !== 'xml_project' &&
      o.value !== 'csv_project' &&
      o.value !== 'pixxi_project',
  );

const ZAPIER_OPTIONS: EnterpriseEnumValue[] = [
  { id: 1, name: 'Disabled', value: 'disabled' },
  { id: 2, name: 'Enabled', value: 'enabled' },
];

const QR_CODE_OPTIONS: EnterpriseEnumValue[] = [
  { id: 1, name: 'QR permit URL', value: 'permit_url' },
  { id: 2, name: 'QR image URL', value: 'image_url' },
];

const PIXXI_LISTING_STATUS_OPTIONS: EnterpriseEnumValue[] = [
  { id: 1, name: 'All', value: 'all' },
  { id: 2, name: 'Active', value: 'active' },
  { id: 3, name: 'Inactive', value: 'inactive' },
  { id: 4, name: 'Sold', value: 'sold' },
];

const KENDAL_LISTING_STATUS_OPTIONS: EnterpriseEnumValue[] = [
  { id: 1, name: 'All', value: 'all' },
  { id: 2, name: 'On-market', value: 'on_market' },
  { id: 3, name: 'Off-market', value: 'off_market' },
];

const PROPFUSION_LISTING_STATUS_OPTIONS: EnterpriseEnumValue[] = [
  { id: 1, name: 'All', value: 'all' },
  { id: 2, name: 'Live', value: 'live' },
];

const PROPERTYFINDER_LISTING_STATUS_OPTIONS: EnterpriseEnumValue[] = [
  { id: 1, name: 'Live', value: 'live' },
  { id: 2, name: 'Live + Draft', value: 'live_draft' },
  { id: 3, name: 'Draft', value: 'draft' },
];

const PIXXI_FORM_TYPE_OPTIONS: EnterpriseEnumValue[] = [
  { id: 1, name: 'Listing (recommended)', value: 'listing' },
  { id: 2, name: 'General', value: 'general' },
];

// Parser-type → which fields are visible. Keeps the JSX below readable.
function flagsFor(parser: string | null | undefined) {
  const v = parser ?? '';
  const isPixxi = v === 'pixxi_unit' || v === 'pixxi_project';
  const isKendal = v === 'kendal_unit' || v === 'kendal_project';
  const isPropfusion = v === 'propfusion_unit' || v === 'propfusion_project';
  const isPropspace = v === 'propspace_unit';
  const isPropertyFinder = v === 'propertyfinder_unit';
  const isGoyzer = v === 'goyzer_unit';
  const isXmlOrCsv =
    v === 'xml' ||
    v === 'xml_project' ||
    v === 'csv_unit' ||
    v === 'csv_project';
  return {
    isPixxi,
    isKendal,
    isPropfusion,
    isPropspace,
    isPropertyFinder,
    isGoyzer,
    isXmlOrCsv,
    showMainUrl: !isPropertyFinder, // PF is API-only — no URL field
    showApiKey: isPropspace || isPropertyFinder,
    showApiSecret: isPropspace || isPropertyFinder,
    showApiToken: isPixxi || isKendal,
    showKendalIds: isKendal,
    showPropfusionUrl: isPropfusion,
    showGoyzer: isGoyzer,
    showQrMethod: isPixxi,
    showPixxiListingStatus: isPixxi,
    showKendalListingStatus: isKendal,
    showPropfusionListingStatus: isPropfusion,
    showPropertyFinderListingStatus: isPropertyFinder,
    showPixxiForm: isPixxi,
    // Extra-datasource list applies to URL-based parsers only.
    showExtras: isXmlOrCsv,
  };
}

export function DatasourcesView() {
  const { enterpriseId, agency, setAgency } = useAgencyContext();
  const [saving, setSaving] = React.useState<Set<string>>(() => new Set());
  const [saveError, setSaveError] = React.useState<string | null>(null);

  // NOTE: hooks below (`useStringField` calls) MUST run on every render.
  // Early-returning here would change the hook count between the loading
  // render and the loaded render → React crashes the whole tree.
  async function patch<K extends keyof EnterpriseSettingsUpdateInput>(
    key: K,
    value: EnterpriseSettingsUpdateInput[K],
  ) {
    setSaveError(null);
    setSaving((prev) => {
      const next = new Set(prev);
      next.add(String(key));
      return next;
    });
    setAgency((prev) =>
      prev ? ({ ...prev, [key]: value } as EnterpriseDetail) : prev,
    );
    try {
      await client.admin.updateEnterprise(enterpriseId, {
        [key]: value,
      } as EnterpriseSettingsUpdateInput);
    } catch (err) {
      setSaveError((err as Error).message);
      try {
        const fresh = await client.admin.getEnterprise(enterpriseId);
        setAgency(fresh);
      } catch {
        /* noop */
      }
    } finally {
      setSaving((prev) => {
        const next = new Set(prev);
        next.delete(String(key));
        return next;
      });
    }
  }

  // String-field hook with onBlur-save semantics. Local state mirrors the
  // input so React doesn't fight typing; only commits on blur if changed.
  function useStringField(
    key: keyof EnterpriseDetail & keyof EnterpriseSettingsUpdateInput,
  ) {
    const remote = (agency?.[key] as string | null | undefined) ?? '';
    const [local, setLocal] = React.useState<string>(remote);
    React.useEffect(() => {
      setLocal(remote);
    }, [remote]);
    return {
      value: local,
      onChange: (v: string) => setLocal(v),
      onBlur: () => {
        const trimmed = local.trim();
        const next = trimmed === '' ? null : trimmed;
        if (next !== (agency?.[key] ?? null)) {
          void patch(
            key,
            next as unknown as EnterpriseSettingsUpdateInput[typeof key],
          );
        }
      },
    };
  }

  function selectEnumValue(
    key: keyof EnterpriseDetail & keyof EnterpriseSettingsUpdateInput,
    options: EnterpriseEnumValue[],
    selectedValue: string,
  ) {
    const opt = options.find((o) => o.value === selectedValue);
    if (!opt) return;
    void patch(
      key,
      opt as unknown as EnterpriseSettingsUpdateInput[typeof key],
    );
  }

  const xmlUrl = useStringField('xmlUrl');
  const apiKey = useStringField('apiKey');
  const apiSecret = useStringField('apiSecret');
  const apiToken = useStringField('apiToken');
  const kendalUserID = useStringField('kendalUserID');
  const kendalOrgID = useStringField('kendalOrgID');
  const propfusionURL = useStringField('propfusionURL');
  const goyzerAccessCode = useStringField('goyzerAccessCode');
  const goyzerGroupCode = useStringField('goyzerGroupCode');
  const goyzerMediaName = useStringField('goyzerMediaName');
  const goyzerMethodOfContact = useStringField('goyzerMethodOfContact');
  const goyzerFallbackBrokerID = useStringField('goyzerFallbackBrokerID');
  const formId = useStringField('formId');
  const zapierEndpoint = useStringField('zapierEndpoint');

  // All hooks have run — safe to early-return now.
  if (!agency) {
    return (
      <Card className="p-6">
        <div className="font-mono text-[12px] text-muted-foreground">
          Loading…
        </div>
      </Card>
    );
  }

  const isLegacy = agency.legacy === true;
  const parserOptions = isLegacy ? PARSER_TYPES_FULL : PARSER_TYPES_NON_LEGACY;
  const f = flagsFor(agency.parserType?.value);

  // Extra datasources — local edits stay local until blur, then we push the
  // whole array up. New rows start with an empty url so the user types into
  // it; removal patches immediately.
  const extras = agency.xmlFeeds ?? [];
  function updateExtra(id: string | number, url: string) {
    const next = extras.map((e) => (e.id === id ? { ...e, url } : e));
    void patch('xmlFeeds', next);
  }
  function removeExtra(id: string | number) {
    void patch('xmlFeeds', extras.filter((e) => e.id !== id));
  }
  function addExtra() {
    void patch('xmlFeeds', [
      ...extras,
      {
        id: Math.floor(Math.random() * 1e13),
        url: '',
        updated: new Date().toISOString(),
      },
    ]);
  }

  // Main URL field label changes for Pixxi (it stores company-name there,
  // not a URL).
  const mainUrlLabel = f.isPixxi
    ? 'Company name in Pixxi'
    : 'Main datasource URL';

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="space-y-6">
          <div>
            <SectionHeading title="Datasources" />
            <p className="mt-1 font-sans text-[13px] text-muted-foreground">
              Link external datasources to the enterprise.
            </p>
          </div>

          <Field label="Parser type" saving={saving.has('parserType')} accent>
            <Select<string>
              options={parserOptions.map((o) => ({
                value: o.value,
                label: o.name,
              }))}
              value={agency.parserType?.value ?? ''}
              placeholder="Select parser"
              onChange={(v) =>
                selectEnumValue('parserType', parserOptions, v)
              }
            />
          </Field>

          {f.showMainUrl && (
            <Field label={mainUrlLabel} saving={saving.has('xmlUrl')}>
              <Input
                {...xmlUrl}
                onChange={(e) => xmlUrl.onChange(e.target.value)}
              />
            </Field>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            {f.showApiToken && (
              <Field label="API token" saving={saving.has('apiToken')}>
                <Input
                  {...apiToken}
                  onChange={(e) => apiToken.onChange(e.target.value)}
                />
              </Field>
            )}
            {f.showApiKey && (
              <Field
                label="API key"
                sublabel={f.isPropspace ? '(lead push)' : undefined}
                saving={saving.has('apiKey')}
              >
                <Input
                  {...apiKey}
                  onChange={(e) => apiKey.onChange(e.target.value)}
                />
              </Field>
            )}
            {f.showApiSecret && (
              <Field
                label="API secret"
                sublabel={f.isPropspace ? '(lead push)' : undefined}
                saving={saving.has('apiSecret')}
              >
                <Input
                  {...apiSecret}
                  onChange={(e) => apiSecret.onChange(e.target.value)}
                />
              </Field>
            )}
            {f.showKendalIds && (
              <>
                <Field
                  label="User ID"
                  sublabel="(lead push)"
                  saving={saving.has('kendalUserID')}
                >
                  <Input
                    {...kendalUserID}
                    onChange={(e) => kendalUserID.onChange(e.target.value)}
                  />
                </Field>
                <Field
                  label="Org ID"
                  sublabel="(lead push)"
                  saving={saving.has('kendalOrgID')}
                >
                  <Input
                    {...kendalOrgID}
                    onChange={(e) => kendalOrgID.onChange(e.target.value)}
                  />
                </Field>
              </>
            )}
            {f.showPropfusionUrl && (
              <Field
                label="Endpoint URL"
                sublabel="(lead push)"
                saving={saving.has('propfusionURL')}
              >
                <Input
                  {...propfusionURL}
                  onChange={(e) => propfusionURL.onChange(e.target.value)}
                />
              </Field>
            )}
            {f.showGoyzer && (
              <>
                <Field
                  label="Access code"
                  sublabel="(lead push)"
                  saving={saving.has('goyzerAccessCode')}
                >
                  <Input
                    {...goyzerAccessCode}
                    onChange={(e) => goyzerAccessCode.onChange(e.target.value)}
                  />
                </Field>
                <Field
                  label="Group code"
                  sublabel="(lead push)"
                  saving={saving.has('goyzerGroupCode')}
                >
                  <Input
                    {...goyzerGroupCode}
                    onChange={(e) => goyzerGroupCode.onChange(e.target.value)}
                  />
                </Field>
                <Field
                  label="Media name"
                  sublabel="(lead push)"
                  saving={saving.has('goyzerMediaName')}
                >
                  <Input
                    {...goyzerMediaName}
                    onChange={(e) => goyzerMediaName.onChange(e.target.value)}
                  />
                </Field>
                <Field
                  label="Method of contact"
                  sublabel="(lead push)"
                  saving={saving.has('goyzerMethodOfContact')}
                >
                  <Input
                    {...goyzerMethodOfContact}
                    onChange={(e) =>
                      goyzerMethodOfContact.onChange(e.target.value)
                    }
                  />
                </Field>
                <Field
                  label="Fallback agent ID"
                  sublabel="(lead push: overrides listing agent)"
                  saving={saving.has('goyzerFallbackBrokerID')}
                >
                  <Input
                    {...goyzerFallbackBrokerID}
                    onChange={(e) =>
                      goyzerFallbackBrokerID.onChange(e.target.value)
                    }
                  />
                </Field>
              </>
            )}
            {f.showQrMethod && (
              <Field
                label="QR code method"
                saving={saving.has('qrCodeMethod')}
              >
                <Select<string>
                  options={QR_CODE_OPTIONS.map((o) => ({
                    value: o.value,
                    label: o.name,
                  }))}
                  value={agency.qrCodeMethod?.value ?? 'permit_url'}
                  onChange={(v) =>
                    selectEnumValue('qrCodeMethod', QR_CODE_OPTIONS, v)
                  }
                />
              </Field>
            )}
            {f.showPixxiListingStatus && (
              <Field
                label="Include status"
                saving={saving.has('pixxiListingStatusInclude')}
              >
                <Select<string>
                  options={PIXXI_LISTING_STATUS_OPTIONS.map((o) => ({
                    value: o.value,
                    label: o.name,
                  }))}
                  value={agency.pixxiListingStatusInclude?.value ?? 'all'}
                  onChange={(v) =>
                    selectEnumValue(
                      'pixxiListingStatusInclude',
                      PIXXI_LISTING_STATUS_OPTIONS,
                      v,
                    )
                  }
                />
              </Field>
            )}
            {f.showKendalListingStatus && (
              <Field
                label="Include status"
                saving={saving.has('kendalListingStatusInclude')}
              >
                <Select<string>
                  options={KENDAL_LISTING_STATUS_OPTIONS.map((o) => ({
                    value: o.value,
                    label: o.name,
                  }))}
                  value={agency.kendalListingStatusInclude?.value ?? 'all'}
                  onChange={(v) =>
                    selectEnumValue(
                      'kendalListingStatusInclude',
                      KENDAL_LISTING_STATUS_OPTIONS,
                      v,
                    )
                  }
                />
              </Field>
            )}
            {f.showPropfusionListingStatus && (
              <Field
                label="Include status"
                saving={saving.has('propfusionListingStatusInclude')}
              >
                <Select<string>
                  options={PROPFUSION_LISTING_STATUS_OPTIONS.map((o) => ({
                    value: o.value,
                    label: o.name,
                  }))}
                  value={agency.propfusionListingStatusInclude?.value ?? 'all'}
                  onChange={(v) =>
                    selectEnumValue(
                      'propfusionListingStatusInclude',
                      PROPFUSION_LISTING_STATUS_OPTIONS,
                      v,
                    )
                  }
                />
              </Field>
            )}
            {f.showPropertyFinderListingStatus && (
              <Field
                label="Include status"
                saving={saving.has('propertyFinderListingStatusInclude')}
              >
                <Select<string>
                  options={PROPERTYFINDER_LISTING_STATUS_OPTIONS.map((o) => ({
                    value: o.value,
                    label: o.name,
                  }))}
                  value={
                    agency.propertyFinderListingStatusInclude?.value ?? 'live'
                  }
                  onChange={(v) =>
                    selectEnumValue(
                      'propertyFinderListingStatusInclude',
                      PROPERTYFINDER_LISTING_STATUS_OPTIONS,
                      v,
                    )
                  }
                />
              </Field>
            )}
            {f.showPixxiForm && (
              <>
                <Field
                  label="Pixxi form type"
                  sublabel="(select same in Pixxi)"
                  saving={saving.has('pixxiFormType')}
                >
                  <Select<string>
                    options={PIXXI_FORM_TYPE_OPTIONS.map((o) => ({
                      value: o.value,
                      label: o.name,
                    }))}
                    value={agency.pixxiFormType?.value ?? 'listing'}
                    onChange={(v) =>
                      selectEnumValue(
                        'pixxiFormType',
                        PIXXI_FORM_TYPE_OPTIONS,
                        v,
                      )
                    }
                  />
                </Field>
                <Field
                  label="Pixxi form ID"
                  sublabel="(lead push)"
                  saving={saving.has('formId')}
                >
                  <Input
                    {...formId}
                    onChange={(e) => formId.onChange(e.target.value)}
                  />
                </Field>
              </>
            )}
          </div>

          {f.showExtras && (
            <div className="space-y-3">
              {extras.map((extra) => (
                <ExtraDatasourceRow
                  key={String(extra.id)}
                  initialUrl={extra.url}
                  onCommit={(url) => updateExtra(extra.id, url)}
                  onRemove={() => removeExtra(extra.id)}
                  saving={saving.has('xmlFeeds')}
                />
              ))}
              <Button variant="ghost" size="sm" onClick={addExtra}>
                <PlusCircle />
                Add extra datasource
              </Button>
            </div>
          )}
        </div>
      </Card>

      <CustomParsersCard
        agency={agency}
        saving={saving.has('customParsers')}
        onChange={(next) => void patch('customParsers', next)}
      />

      <Card className="p-6">
        <div className="space-y-6">
          <SectionHeading title="CRM notifications" />

          <div className="grid gap-4 md:grid-cols-3">
            <Field label="Zapier push" saving={saving.has('zapierPush')}>
              <Select<string>
                options={ZAPIER_OPTIONS.map((o) => ({
                  value: o.value,
                  label: o.name,
                }))}
                value={agency.zapierPush?.value ?? 'disabled'}
                onChange={(v) =>
                  selectEnumValue('zapierPush', ZAPIER_OPTIONS, v)
                }
              />
            </Field>
            {agency.zapierPush?.value === 'enabled' && (
              <Field
                label="Catch hook"
                saving={saving.has('zapierEndpoint')}
                className="md:col-span-2"
              >
                <Input
                  {...zapierEndpoint}
                  onChange={(e) => zapierEndpoint.onChange(e.target.value)}
                />
              </Field>
            )}
          </div>
        </div>
      </Card>

      {saveError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 font-mono text-[11px] text-destructive">
          {saveError}
        </div>
      )}
    </div>
  );
}

// Extra datasource row keeps its URL local until blur — same pattern as the
// other text fields, but committing pushes the whole xmlFeeds array up.
function ExtraDatasourceRow({
  initialUrl,
  onCommit,
  onRemove,
  saving,
}: {
  initialUrl: string;
  onCommit: (url: string) => void;
  onRemove: () => void;
  saving?: boolean;
}) {
  const [local, setLocal] = React.useState<string>(initialUrl);
  React.useEffect(() => {
    setLocal(initialUrl);
  }, [initialUrl]);
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <Label className="font-sans text-[13px] font-medium normal-case tracking-normal text-foreground">
          Extra datasource URL
          <span className="ml-1 font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
            (inherits parser type)
          </span>
        </Label>
        {saving && (
          <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
        )}
        <button
          type="button"
          onClick={onRemove}
          className="ml-auto inline-flex items-center gap-1 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-destructive transition-colors hover:text-destructive/80"
        >
          <Trash2 className="h-3 w-3" />
          Remove
        </button>
      </div>
      <Input
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => {
          if (local !== initialUrl) onCommit(local);
        }}
      />
    </div>
  );
}

// Brokerage-specific feed parsers. Enabled ones appear in this agency's
// CRM settings (and only theirs). Never set, the agency keeps the custom
// parser its legacy datasource used; the first toggle makes the list
// explicit.
function CustomParsersCard({
  agency,
  saving,
  onChange,
}: {
  agency: EnterpriseDetail;
  saving: boolean;
  onChange: (next: string[]) => void;
}) {
  const legacySource = {
    parserType: agency.parserType,
    parserId: agency.parserId,
    name: agency.name,
  };
  const effective = allowedCustomParsers({
    ...legacySource,
    customParsers: agency.customParsers ?? undefined,
  });
  const fromLegacy = agency.customParsers == null ? legacyCustomParser(legacySource) : null;
  const toggle = (key: string, on: boolean) =>
    onChange(on ? [...new Set([...effective, key])] : effective.filter((k) => k !== key));

  return (
    <Card className="p-6">
      <div className="space-y-4">
        <div>
          <SectionHeading title="Custom parsers" />
          <p className="mt-1 font-sans text-[13px] text-muted-foreground">
            Feed parsers built for one brokerage. Enabled ones show up in this
            agency&apos;s CRM settings; no other agency sees them.
          </p>
        </div>
        <div className="divide-y divide-border rounded-lg border border-border">
          {CUSTOM_PARSERS.map((p) => (
            <div key={p.key} className="flex items-center justify-between gap-4 px-4 py-3">
              <div>
                <div className="font-sans text-[14px] font-medium">{p.label}</div>
                <div className="font-sans text-[12px] text-muted-foreground">
                  {!p.ready
                    ? 'Coming soon — not ported to the new sync yet'
                    : fromLegacy === p.key
                      ? "On from this agency's legacy datasource"
                      : null}
                </div>
              </div>
              <Switch
                checked={effective.includes(p.key)}
                disabled={!p.ready || saving}
                onCheckedChange={(on) => toggle(p.key, on)}
                aria-label={p.label}
              />
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

function SectionHeading({ title }: { title: string }) {
  return (
    <h2 className="font-display text-[20px] font-medium leading-none tracking-[-0.01em]">
      {title}
    </h2>
  );
}

function Field({
  label,
  sublabel,
  saving,
  children,
  className,
  accent,
}: {
  label: string;
  sublabel?: string;
  saving?: boolean;
  children: React.ReactNode;
  className?: string;
  accent?: boolean;
}) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <div className="flex items-center gap-2">
        <Label
          className={cn(
            'font-sans text-[13px] font-medium normal-case tracking-normal',
            accent ? 'text-primary' : 'text-foreground',
          )}
        >
          {label}
          {sublabel && (
            <span className="ml-1 font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
              {sublabel}
            </span>
          )}
        </Label>
        {saving && (
          <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
        )}
      </div>
      {children}
    </div>
  );
}
